import { useEffect, useRef, useState } from "react";
import { Alert, Autocomplete, Box, Button, Card, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import Page from "../../../components/Page";
import dashboardService, { CustomerExecutive, CustomerOption, Treatment } from "./services/dashboardService";

const NAVY = "#004A78";
const COLORS = ["#004A78", "#2E7D32", "#F9A825", "#6A1B9A", "#00838F", "#C62828"];
const DASHBOARD_BRANCHES = ["NE", "SP", "PA"];
const BRANCH_COLORS: Record<string, string> = {
  NE: "#004A78",
  SP: "#2E7D32",
  PA: "#F9A825",
};

const MONTHS = [
  { value: 1, label: "Janeiro" },
  { value: 2, label: "Fevereiro" },
  { value: 3, label: "Março" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Maio" },
  { value: 6, label: "Junho" },
  { value: 7, label: "Julho" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Setembro" },
  { value: 10, label: "Outubro" },
  { value: 11, label: "Novembro" },
  { value: 12, label: "Dezembro" },
];

const YEARS = Array.from({ length: new Date().getFullYear() - 2019 }, (_, index) => 2020 + index);

const money = (value: number) =>
  Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const pairs = (value: number) =>
  Number(value || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 });

const percent = (value: number | null) =>
  value == null
    ? "—"
    : `${(value * 100).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`;

const ChartCard = ({ title, children }: { title: string; children: any }) => (
  <Card sx={{ p: 2, minHeight: 360, height: 360, display: "flex", flexDirection: "column" }}>
    <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 700, mb: 1 }}>
      {title}
    </Typography>
    <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
  </Card>
);

const MonthSelect = ({
  label,
  month,
  year,
  onMonth,
  onYear,
}: {
  label: string;
  month: number;
  year: number;
  onMonth: (value: number) => void;
  onYear: (value: number) => void;
}) => (
  <Stack direction="row" spacing={1}>
    <FormControl size="small" sx={{ minWidth: 0, flex: 1 }}>
      <InputLabel>{label}</InputLabel>
      <Select value={month} label={label} onChange={(event) => onMonth(Number(event.target.value))}>
        {MONTHS.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    <FormControl size="small" sx={{ minWidth: 85 }}>
      <InputLabel>Ano</InputLabel>
      <Select value={year} label="Ano" onChange={(event) => onYear(Number(event.target.value))}>
        {YEARS.map((item) => (
          <MenuItem key={item} value={item}>
            {item}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Stack>
);

const customerLabel = (customer: CustomerOption) => `${customer.initials || "Sem sigla"} — ${customer.name}`;
const EmptyChart = () => <Box sx={{ p: 3, textAlign: "center" }}><Typography color="text.secondary">Sem movimento no período.</Typography></Box>;

export default function CustomerExecutiveDashboard() {
  const now = new Date();
  const [customer, setCustomer] = useState<CustomerOption | null>(null);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<CustomerOption[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchRetry, setSearchRetry] = useState(0);
  const [mode, setMode] = useState<"month" | "range">("month");
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [fromMonth, setFromMonth] = useState(1);
  const [fromYear, setFromYear] = useState(2020);
  const [toMonth, setToMonth] = useState(now.getMonth() + 1);
  const [toYear, setToYear] = useState(now.getFullYear());
  const [regime, setRegime] = useState<"all" | "postpaid" | "prepaid">("all");
  const [ranking, setRanking] = useState<"billed" | "pairs">("billed");
  const [data, setData] = useState<CustomerExecutive | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestId = useRef(0);

  useEffect(() => {
    let active = true;
    setSearchLoading(true);
    setSearchError("");
    const timer = window.setTimeout(async () => {
      try {
        const response = await dashboardService.searchExecutiveCustomers(search);
        if (active) setOptions(response.data);
      } catch {
        if (active) { setOptions([]); setSearchError("Não foi possível buscar clientes. Tente novamente."); }
      } finally { if (active) setSearchLoading(false); }
    }, 300);
    return () => { active = false; window.clearTimeout(timer); };
  }, [search, searchRetry]);

  const load = async () => {
    const id = ++requestId.current;
    setData(null);
    setError("");
    if (!customer) { setLoading(false); return; }
    setLoading(true);
    try {
      const response = await dashboardService.getCustomerExecutive({
        customerId: customer.customerId, mode,
        ...(mode === "month" ? { month, year } : { fromMonth, fromYear, toMonth, toYear }),
      });
      if (id === requestId.current) setData(response.data);
    } catch (failure: any) {
      if (id === requestId.current) setError(failure?.response?.data?.message || "Não foi possível carregar os indicadores. Tente novamente.");
    } finally { if (id === requestId.current) setLoading(false); }
  };
  useEffect(() => {
    load();
    return () => { requestId.current += 1; };
    // Filters also refresh automatically, so displayed numbers always match their labels.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.customerId, mode, month, year, fromMonth, fromYear, toMonth, toYear]);

  const metrics = data?.[regime];
  const kpis = data?.kpis;
  const regimeLabel = regime === "all" ? "Geral" : regime === "postpaid" ? "Pós-pago" : "Pré-pago";
  const treatments = [...(metrics?.treatments || [])].sort((a, b) => b[ranking] - a[ranking] || a.lensId - b.lensId).slice(0, 10);
  const mix = (metrics?.arMix || []).filter(item => item.pairs > 0);
  const orderTypes = [ { name: "Venda", pairs: metrics?.orderTypes.salePairs || 0 }, { name: "Cortesia", pairs: metrics?.orderTypes.courtesyPairs || 0 } ];
  const hasOrders = orderTypes.some(item => item.pairs > 0);
  const history = metrics?.monthlySeries || [];
  const branches = Array.from(new Set([...DASHBOARD_BRANCHES, ...history.flatMap(item => Object.keys(item).filter(key => !["month", "total"].includes(key)))]));
  const interval = "preserveStartEnd" as const;
  const scope = data?.period.mode === "range" ? "período" : "mês";
  const cards = [
    ["Movimento comercial", money((kpis?.postpaidBilled || 0) + (kpis?.prepaidConsumed || 0)), "Pós faturado + pré consumido"],
    ["Pós-pago faturado", money(kpis?.postpaidBilled || 0), `No ${scope}`],
    ["Pré-pago consumido", money(kpis?.prepaidConsumed || 0), `No ${scope}`],
    ["Saldo pré-pago", kpis?.prepaidBalance == null ? "Não se aplica" : money(kpis.prepaidBalance), kpis?.prepaidBalance == null ? "Cliente pós-pago" : "Saldo ao fim do período"],
    ["Carteira pós-paga", money(kpis?.openOrders || 0), "Posição atual · OS sem fechamento"],
  ];
  const treatmentTooltip = ({ active, payload }: any) => {
    const treatment: Treatment | undefined = payload?.[0]?.payload;
    if (!active || !treatment) return null;
    return <Card sx={{ p: 1.5, maxWidth: 280 }}><Typography variant="subtitle2">{treatment.name}</Typography><Typography>{money(treatment.billed)}</Typography><Typography>{pairs(treatment.pairs)} pares</Typography></Card>;
  };

  return <Page title="Visão por cliente | Opto"><Box sx={{ p: { xs: 1, md: 3 } }}>
    <Typography variant="h5" component="h1" sx={{ color: NAVY, fontWeight: 700, mb: 2 }}>Visão por cliente</Typography>
    <Stack spacing={1.5} sx={{ mb: 2 }}>
      <Autocomplete options={options} value={customer} loading={searchLoading} filterOptions={items => items}
        getOptionLabel={customerLabel} isOptionEqualToValue={(option, value) => option.customerId === value.customerId}
        onChange={(_event, value) => { requestId.current += 1; setData(null); setCustomer(value); }}
        onInputChange={(_event, value, reason) => { if (reason === "input" || reason === "clear") setSearch(value); }}
        noOptionsText={searchError || "Nenhum cliente encontrado"} loadingText="Buscando clientes…"
        renderInput={params => <TextField {...params} size="small" label="Cliente" placeholder="Digite o nome ou a sigla" helperText="Busque e selecione um cliente. Todas as unidades serão consideradas." />}
      />
      {searchError && <Alert severity="error" action={<Button color="inherit" onClick={() => setSearchRetry(value => value + 1)}>Tentar novamente</Button>}>{searchError}</Alert>}
      <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25} sx={{ flexWrap: "wrap" }}>
        <ToggleButtonGroup exclusive size="small" value={mode} aria-label="Tipo de período" onChange={(_event, value) => value && setMode(value)}>
          <ToggleButton value="month">Mês específico</ToggleButton><ToggleButton value="range">Visão geral</ToggleButton>
        </ToggleButtonGroup>
        {mode === "month" ? <MonthSelect label="Mês" month={month} year={year} onMonth={setMonth} onYear={setYear} /> : <>
          <MonthSelect label="De" month={fromMonth} year={fromYear} onMonth={setFromMonth} onYear={setFromYear} />
          <MonthSelect label="Até" month={toMonth} year={toYear} onMonth={setToMonth} onYear={setToYear} />
        </>}
        <ToggleButtonGroup exclusive size="small" value={regime} aria-label="Regime do cliente" onChange={(_event, value) => value && setRegime(value)}>
          <ToggleButton value="all">Todos</ToggleButton><ToggleButton value="postpaid">Pós-pago</ToggleButton><ToggleButton value="prepaid">Pré-pago</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="contained" disabled={!customer || loading} onClick={load} sx={{ bgcolor: NAVY }}>Atualizar</Button>
      </Stack>
    </Stack>
    {!customer ? <Card sx={{ p: 4, textAlign: "center" }}><Typography>Selecione um cliente para visualizar os indicadores</Typography></Card>
      : loading ? <Box role="status" sx={{ py: 8, textAlign: "center" }}><CircularProgress aria-label="Carregando indicadores" /><Typography>Carregando indicadores…</Typography></Box>
      : error ? <Alert severity="error" action={<Button color="inherit" onClick={load}>Tentar novamente</Button>}>{error}</Alert>
      : data && metrics && <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }} aria-live="polite">{customerLabel(data.customer)} · {data.period.label} · {regimeLabel} nos gráficos</Typography>
        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>{cards.map(([label, value, hint]) => <Grid item xs={12} sm={6} md={2.4} key={label}>
          <Card sx={{ p: 2, height: "100%" }}><Typography variant="caption" color="text.secondary">{label}</Typography><Typography variant="h6" sx={{ color: NAVY, fontWeight: 800 }}>{value}</Typography><Typography variant="caption" color="text.secondary">{hint}</Typography></Card>
        </Grid>)}</Grid>
        <Grid container spacing={1.5}>
          <Grid item xs={12} lg={8}><ChartCard title={`${regimeLabel} — ${regime === "prepaid" ? "consumo" : "movimento comercial"} ${mode === "range" ? "no período" : "em 12 meses"}`}>
            {history.some(item => item.total !== 0) ? <ResponsiveContainer><BarChart data={history}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" tick={{ fontSize: 11 }} interval={interval} minTickGap={24} /><YAxis tickFormatter={value => pairs(Number(value))} width={65} /><Tooltip formatter={(value: any) => money(Number(value))} /><Legend />{branches.map((branch, index) => <Bar isAnimationActive={false} key={branch} dataKey={branch} stackId="a" fill={BRANCH_COLORS[branch] || COLORS[index % COLORS.length]} />)}</BarChart></ResponsiveContainer> : <EmptyChart />}
          </ChartCard></Grid>
          <Grid item xs={12} lg={4}><ChartCard title={`${regimeLabel} — mix AR do ${scope}`}>
            {mix.length ? <ResponsiveContainer><PieChart><Pie isAnimationActive={false} data={mix} dataKey="pairs" nameKey="label" innerRadius={50} outerRadius={80}>{mix.map(item => <Cell key={item.key} fill={COLORS[(metrics.arMix.findIndex(product => product.key === item.key)) % COLORS.length]} />)}</Pie><Tooltip formatter={(value: any) => `${pairs(Number(value))} pares`} /><Legend /></PieChart></ResponsiveContainer> : <EmptyChart />}
          </ChartCard></Grid>
          <Grid item xs={12}><Card sx={{ p: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" sx={{ mb: 1 }}><Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 700 }}>Top 10 tratamentos — {regimeLabel.toLowerCase()}</Typography><ToggleButtonGroup exclusive size="small" value={ranking} aria-label="Ordenar tratamentos por" onChange={(_event, value) => value && setRanking(value)}><ToggleButton value="billed">Valor (R$)</ToggleButton><ToggleButton value="pairs">Pares</ToggleButton></ToggleButtonGroup></Stack>
            {treatments.length ? <Box sx={{ height: Math.max(260, treatments.length * 40) }}><ResponsiveContainer><BarChart data={treatments} layout="vertical" margin={{ left: 0, right: 16 }}><CartesianGrid strokeDasharray="3 3" /><XAxis type="number" tickFormatter={value => pairs(Number(value))} /><YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} tickFormatter={value => String(value).length > 18 ? `${String(value).slice(0, 17)}…` : String(value)} /><Tooltip content={treatmentTooltip} /><Bar isAnimationActive={false} dataKey={ranking} name={ranking === "billed" ? "Valor (R$)" : "Pares"} fill={NAVY} /></BarChart></ResponsiveContainer></Box> : <EmptyChart />}
          </Card></Grid>
          <Grid item xs={12} md={regime === "all" ? 8 : 12}><ChartCard title={`% premium — ${mode === "range" ? "período" : "12 meses"}`}><ResponsiveContainer><LineChart data={metrics.premiumSeries}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" tick={{ fontSize: 11 }} interval={interval} minTickGap={24} /><YAxis tickFormatter={value => `${Math.round(Number(value) * 100)}%`} domain={[0, 1]} /><Tooltip formatter={(value: any) => percent(Number(value))} /><Line isAnimationActive={false} type="monotone" dataKey="premiumShare" name="Premium" stroke="#2E7D32" strokeWidth={2} dot={false} /></LineChart></ResponsiveContainer></ChartCard></Grid>
          {regime === "all" && <Grid item xs={12} md={4}><ChartCard title={`Venda vs cortesia (pares no ${scope})`}>{hasOrders ? <ResponsiveContainer><PieChart><Pie isAnimationActive={false} data={orderTypes} dataKey="pairs" nameKey="name" innerRadius={50} outerRadius={80}><Cell fill={NAVY} /><Cell fill="#607D8B" /></Pie><Tooltip formatter={(value: any) => `${pairs(Number(value))} pares`} /><Legend /></PieChart></ResponsiveContainer> : <EmptyChart />}</ChartCard></Grid>}
        </Grid>
      </>}
  </Box></Page>;
}
